import React from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  IconButton,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import NotificationSoundIcon from "../../assets/notifications/NotificationSoundIcon.svg";
import CloseIcon from "@mui/icons-material/Close";
import { Notification } from "../../redux/features/slices/notificationSlice";
import { useAppDispatch } from "../../redux/hooks";
import {
  removeNotification,
  markAsRead,
} from "../../redux/features/slices/notificationSlice";
import { markNotificationAsReadAPI } from "../../api/notificationAPI";
import { useRole } from "../../context/RoleContext";
import {
  NotificationDropdown,
  NotificationCard,
  EmptyStateContainer,
  EmptyStateText,
  NotificationsList,
  NotificationCardStyles,
  NotificationIconContainer,
  NotificationContent,
  NotificationHeader,
  ChipStyles,
  DateText,
  ProductNameText,
  MessageText,
  CloseButtonStyles,
  HeaderContainer,
  HeaderText,
} from "./NotificationsDropdown.styles";

interface NotificationsDropdownProps {
  notifications: Notification[];
  customerId: string;
  onClose: () => void;
}

const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({
  notifications,
  customerId,
  onClose,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { role } = useRole();
  const orgId = localStorage.getItem("orgId") ?? "";

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-GB");

  const handleNotificationClick = async (notification: Notification) => {
    try {
      const result = await markNotificationAsReadAPI(notification.id);
      if (result.success) {
        dispatch(markAsRead(notification.id));
        queryClient.invalidateQueries({
          queryKey: ["notifications", customerId],
          exact: true,
        });
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }

    if (notification.productId) {
      navigate(`/${role}/products/product/${notification.productId}`);
    }
    onClose();
  };

  const handleRemoveNotification = async (id: string) => {
    try {
      const result = await markNotificationAsReadAPI(id);
      if (result.success) {
        dispatch(markAsRead(id));

        queryClient.invalidateQueries({
          queryKey: ["notifications", customerId],
          exact: true,
        });
      }
    } catch (error) {
      console.error("Failed remove and mark notification as read:", error);
    }
    dispatch(removeNotification(id));
  };

  return (
    <NotificationDropdown>
      <Box sx={HeaderContainer}>
        <Typography variant="h6" sx={HeaderText}>
          Notifications
        </Typography>
      </Box>

      {notifications.length === 0 ? (
        <Box sx={EmptyStateContainer}>
          <Typography variant="body2" sx={EmptyStateText}>
            You&apos;re all caught up! No new notifications.
          </Typography>
        </Box>
      ) : (
        <List disablePadding sx={NotificationsList}>
          {notifications.map((n) => (
            <NotificationCard key={n.id} sx={NotificationCardStyles}>
              <Box>
                <img
                  src={NotificationSoundIcon}
                  alt="notification"
                  style={NotificationIconContainer}
                />
              </Box>

              <ListItemButton
                sx={NotificationContent}
                onClick={() => handleNotificationClick(n)}
              >
                <Box width="100%">
                  <Box sx={NotificationHeader}>
                    <Chip size="small" label="Back in stock" sx={ChipStyles} />

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={DateText}
                    >
                      {formatDate(n.createdAt)}
                    </Typography>
                  </Box>

                  <Typography
                    variant="subtitle1"
                    fontWeight={500}
                    sx={ProductNameText}
                  >
                    {n.productName}
                  </Typography>

                  <Typography variant="body2" sx={MessageText}>
                    Good news! This item has been restocked and you can place
                    your order now.
                  </Typography>
                </Box>
              </ListItemButton>

              <IconButton
                size="small"
                sx={CloseButtonStyles}
                onClick={() => handleRemoveNotification(n.id)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </NotificationCard>
          ))}
        </List>
      )}
    </NotificationDropdown>
  );
};

export default NotificationsDropdown;
